import React, { useEffect, useState } from 'react';
import { pluck, values, equals } from 'ramda';
import Seo from 'sharedComponents/Seo';
import { Typography } from '@material-ui/core';
import { Debounce, isPresent } from 'utils/helperMethods';

import { useTranslation } from 'react-i18next';
import { getServerError, getServerErrorsFullMessages } from 'utils/errors';
import useFavoriteLoanOfficers from 'reducers/hooks/realEstateAgent/useFavoriteLoanOfficers';
import useWaitlistLoanOfficers from 'reducers/hooks/realEstateAgent/useWaitlistLoanOfficers';
import useLoanOfficers from 'reducers/hooks/realEstateAgent/useLoanOfficers';
import Alert from 'sharedComponents/Alert';

import Search from './components/Search';
import Tabs from './components/Tabs';
import LoanOfficersList from './components/LoanOfficersList';
import WaitlistLoanOfficersList from './components/WaitlistLoanOfficersList';
import FavoriteLoanOfficersList from './components/FavoriteLoanOfficersList';
import useStyles from './useStyles';

const INITIAL_SEARCH_FIELDS_VALUES = { name: '', company: '', location: '' };
const PER_PAGE = 10;

const TABS_INDEX_MAPPER = {
  all: 0,
  waitlist: 1,
};

const RealEstateAgentLoanOfficers = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    loadFavoriteLoanOfficers,
    favoriteLoanOfficers,
    isFavoriteLoanOfficersLoading,
    isFavoriteLoanOfficersIdle,
    createFavoriteLoanOfficer,
    isFavoriteLoanOfficerCreating,
    deleteFavoriteLoanOfficer,
    isFavoriteLoanOfficerDeleting,
  } = useFavoriteLoanOfficers();

  const {
    loadLoanOfficers: loadWaitlistLoanOfficers,
    loadMoreLoanOfficers: LoadMoreWaitlistLoanOfficers,
    loanOfficers: waitlistLoanOfficers,
    isLoanOfficersLoading: isWaitlistLoanOfficersLoading,
    isLoanOfficersIdle: isWaitlistLoanOfficersIdle,
    meta: waitlistLoanOfficersMeta,
    totalCount: waitlistLoanOfficersTotalCount,
    approveLoanOfficer,
    batchApproveLoanOfficers,
    isUpdating,
    isBatchUpdating,
  } = useWaitlistLoanOfficers();

  const {
    loadLoanOfficers,
    loadMoreLoanOfficers,
    loanOfficers,
    meta,
    isLoanOfficersLoading,
    isLoanOfficersIdle,
    totalCount,
  } = useLoanOfficers();

  const buildWaitlistSearchParams = () => {
    return { perPage: PER_PAGE };
  };

  useEffect(() => {
    loadFavoriteLoanOfficers();
    loadLoanOfficers();
    const waitlistSearchParams = buildWaitlistSearchParams();
    loadWaitlistLoanOfficers(waitlistSearchParams);
  }, []);

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [searchFieldsValues, setSearchFieldsValues] = useState(INITIAL_SEARCH_FIELDS_VALUES);
  const [selectedLoanOfficersIds, setSelectedLoanOfficersIds] = useState([]);
  const [error, setError] = useState(null);

  const isAllLoanOfficersTab = equals(activeTabIndex, TABS_INDEX_MAPPER.all);
  const isWaitlistTab = equals(activeTabIndex, TABS_INDEX_MAPPER.waitlist);

  const debouncedSearchChange = Debounce(searchFields => {
    loadLoanOfficers(searchFields);
  });

  const handleSearchFieldChange = ({ target: { name, value } }) => {
    const searchValues = { ...searchFieldsValues, [name]: value };
    setSearchFieldsValues(searchValues);
    debouncedSearchChange(searchValues);
  };

  const handleLoadMore = params => {
    loadMoreLoanOfficers({ ...params, ...searchFieldsValues });
  };

  const handleApproveLoanofficer = async id => {
    try {
      await approveLoanOfficer(id, { state: 'approved' });
      loadFavoriteLoanOfficers();
    } catch (err) {
      const { fullErrorMessages } = getServerErrorsFullMessages(err);
      setError(fullErrorMessages);
    }
  };

  const handleBatchUpdateLoanOfficers = async ids => {
    try {
      await batchApproveLoanOfficers({ loanOfficersIds: ids, state: 'approved' });
      loadFavoriteLoanOfficers();
      setSelectedLoanOfficersIds([]);
    } catch (err) {
      const { fullErrorMessages } = getServerErrorsFullMessages(err);
      setError(fullErrorMessages);
    }
  };

  const handleLoadMoreWaitlistLoanOfficers = params => {
    const searchParams = buildWaitlistSearchParams();
    LoadMoreWaitlistLoanOfficers({ ...params, ...searchParams });
  };

  const handleAddFavoriteLoanOfficer = async id => {
    try {
      await createFavoriteLoanOfficer({ id });
      loadFavoriteLoanOfficers();

      const waitlistSearchParams = buildWaitlistSearchParams();
      loadWaitlistLoanOfficers(waitlistSearchParams);
    } catch (err) {
      const message = getServerError(err);
      setError(message);
    }
  };

  const handleDeleteFavoriteLoanOfficer = async id => {
    await deleteFavoriteLoanOfficer(id);
    loadFavoriteLoanOfficers();
  };

  const handleCloseAlert = () => setError(null);

  const favoriteLoanOfficerIds = pluck('id')(favoriteLoanOfficers);

  const handleTabChange = (_event, newValue) => {
    setActiveTabIndex(newValue);
  };

  return (
    <div className={classes.container} id="scrollableMainContainer" key="scrollableMainContainer">
      <Seo
        title={t('meta.RealEstateAgentLoanOfficers.title')}
        description={t('meta.RealEstateAgentLoanOfficers.description')}
      />
      <div className={classes.content}>
        <div className={classes.favoriteLoanOfficers}>
          <Typography className={classes.label} variant="h1">
            {t('components.RealEstateAgentLoanOfficers.myLoanOfficers')}
          </Typography>
          <FavoriteLoanOfficersList
            loanOfficers={favoriteLoanOfficers}
            isLoading={isFavoriteLoanOfficersLoading || isFavoriteLoanOfficersIdle}
            isDeleting={isFavoriteLoanOfficerDeleting}
            onDelete={handleDeleteFavoriteLoanOfficer}
          />
        </div>
        <div className={classes.loanOfficers}>
          <Search values={searchFieldsValues} onChange={handleSearchFieldChange} />
          <Tabs
            currentTabIndex={activeTabIndex}
            tabIndexValues={values(TABS_INDEX_MAPPER)}
            onChange={handleTabChange}
            allTabsBadgeCount={totalCount}
            waitlistTabsBadgeCount={waitlistLoanOfficersTotalCount}
          />
          {isAllLoanOfficersTab && (
            <LoanOfficersList
              loanOfficers={loanOfficers}
              meta={meta}
              isLoading={isLoanOfficersLoading || isLoanOfficersIdle}
              onLoadMore={handleLoadMore}
              onAddFavoriteLoanOfficer={handleAddFavoriteLoanOfficer}
              isAddingFavoriteLoanOfficer={isFavoriteLoanOfficerCreating}
              favoriteLoanOfficerIds={favoriteLoanOfficerIds}
            />
          )}
          {isWaitlistTab && (
            <WaitlistLoanOfficersList
              loanOfficers={waitlistLoanOfficers}
              meta={waitlistLoanOfficersMeta}
              isLoading={isWaitlistLoanOfficersLoading || isWaitlistLoanOfficersIdle}
              onLoadMore={handleLoadMoreWaitlistLoanOfficers}
              onApproveLoanOfficer={handleApproveLoanofficer}
              onBatchApproveLoanofficers={handleBatchUpdateLoanOfficers}
              isApproving={isUpdating}
              isBatchApproving={isBatchUpdating}
              selectedLoanOfficersIds={selectedLoanOfficersIds}
              setSelectedLoanOfficersIds={setSelectedLoanOfficersIds}
            />
          )}
        </div>
      </div>
      {isPresent(error) && <Alert text={error} open severity="error" handleClose={handleCloseAlert} />}
    </div>
  );
};

export default RealEstateAgentLoanOfficers;



// WEBPACK FOOTER //
// ./src/components/RealEstateAgentLoanOfficers/RealEstateAgentLoanOfficers.js